import { useState } from 'react';
import { Box, Button, Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormHeader } from '../components/FormHeader';
import { Panel } from '../components/Panel';
import { useNotice } from '../components/NoticeProvider';
import { useClaudeTasks } from '../features/claudeTasks/useClaudeTasks';
import { describeForm } from '../lib/formText';
import { useAction } from '../lib/useAction';
import { TabStrip } from '../components/TabStrip';
import { initialForm, type QuoteForm } from '../features/quotations/form/quoteForm';
import {
  ChargesSection,
  ConditionsSection,
  CustomerSection,
  RouteSection,
  ShipmentSection,
} from '../features/quotations/form/QuoteFormSections';
import { QuotePreview } from '../features/quotations/form/QuotePreview';

const SECTIONS = {
  'Quotation Details': 'details',
  Pricing: 'pricing',
  'General Conditions': 'conditions',
  'Preview & Generate': 'generate',
} as const;
type Tab = keyof typeof SECTIONS;
const PREVIEW_TABS = ['Preview', 'Download (.docx)', 'Download PDF'] as const;
const NOT_CONNECTED =
  'Downloads are not available yet. Claude makes the PDF from the SERB Word template for your approval.';

/** Fill in a quotation on the left; the page it makes appears on the right as you type */
export function NewQuotationPage() {
  const notice = useNotice();
  const wide = useMediaQuery(useTheme().breakpoints.up('lg'));
  const [form, setForm] = useState<QuoteForm>(initialForm);
  const [tab, setTab] = useState<Tab>('Quotation Details');
  const set = (patch: Partial<QuoteForm>) => setForm((f) => ({ ...f, ...patch }));
  const notYet = () => notice(NOT_CONNECTED, 'info');
  const tasks = useClaudeTasks();
  // the quotation is made by Claude from the SERB template and comes back as an approval card; nothing goes to the customer
  const generate = useAction(tasks.add, {
    success:
      "Sent to Claude. The quotation will come to Approvals for you to check; nothing is sent to the customer. Follow it under Reminders & Calendar → Claude's Tasks.",
  });
  const submit = () =>
    generate.run({
      task: `Prepare this customer quotation from the SERB Word template and put it on the Approvals page as a card. Do not send it.\n${describeForm({ ...form })}`,
      code: form.relatedSrb,
    });
  const jump = (t: Tab) => {
    setTab(t);
    document.getElementById(SECTIONS[t])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const props = { form, set };

  return (
    <Stack spacing={2}>
      <FormHeader
        back={{ to: '/quotations', label: 'Back to Quotations' }}
        title="New Quotation"
        subtitle="Enter the details below and generate a quotation using the SERB template."
      >
        <Button variant="contained" disabled={generate.busy} onClick={submit}>
          Generate Quotation
        </Button>
      </FormHeader>

      <TabStrip tabs={Object.keys(SECTIONS).map((t) => ({ value: t as Tab }))} value={tab} onChange={jump} />

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(380px, 520px)' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2} sx={{ minWidth: 0 }}>
          <Stack spacing={2} id="details" sx={{ scrollMarginTop: 80 }}>
            <CustomerSection {...props} />
            <RouteSection {...props} />
            <ShipmentSection {...props} />
          </Stack>
          <Box id="pricing" sx={{ scrollMarginTop: 80 }}>
            <ChargesSection {...props} />
          </Box>
          <Box id="conditions" sx={{ scrollMarginTop: 80 }}>
            <ConditionsSection {...props} />
          </Box>
          <Box id="generate" sx={{ scrollMarginTop: 80 }}>
            <Panel
              title="Preview & Generate"
              subtitle="Check the preview, then generate. Nothing is sent to the customer from here."
            >
              {!wide && <QuotePreview form={form} />}
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={generate.busy} onClick={submit}>
                  Generate Quotation
                </Button>
                <Button variant="outlined" onClick={notYet}>
                  Download PDF
                </Button>
              </Stack>
            </Panel>
          </Box>
        </Stack>

        {wide && (
          <Box sx={{ position: 'sticky', top: 76, maxHeight: 'calc(100dvh - 92px)', overflowY: 'auto' }}>
            <Panel title="Quotation Preview">
              <TabStrip
                tabs={PREVIEW_TABS.map((t) => ({ value: t }))}
                value="Preview"
                onChange={(t) => t !== 'Preview' && notYet()}
              />
              <QuotePreview form={form} />
            </Panel>
          </Box>
        )}
      </Box>
    </Stack>
  );
}
